/**
 * Plan 24a, Task 3.1 — Boot helper: screen registry + entry-screen decision
 *
 * `bootPreGameScreens` builds the full screen registry mapping every ScreenId to
 * its factory, creates a ScreenRouter, and opens the warmup arena overlay. The
 * legacy landing/onboarding/main-menu screens remain registered but are no
 * longer used as the normal entry flow.
 *
 * The `send` and `subscribe` callables from BootOpts are stored in a closure for
 * sub-tasks 3.2 and 3.3 (lobby navigation wiring).
 */

import type { GsapRegistry } from '../../renderer/vfx/gsap-registry';
import { createScreenRouter } from './router';
import type { ScreenBackTarget, ScreenFactory, ScreenId, ScreenRouter } from './types';
import { createLandingScreen } from './landing';
import { createMainMenuScreen } from './main-menu';
import { createSettingsScreen } from './settings';
import { createLobbyHostScreen, isTouchLobbyLayout } from './lobby-host';
import { createLobbyJoinerScreen } from './lobby-joiner';
import { createLobbyQuickPlayScreen } from './lobby-quick-play';
import {
  createWarmupArenaOverlayScreen,
  type WarmupTutorialInputBridge,
} from './warmup-arena-overlay';
import {
  createCharacterSelectScreen,
  type CharacterCustomizeCameraRequest,
} from './character-select';
import { createOnboardingScreen } from './onboarding';
import { createMinigameAnnounceScreen } from './minigame-announce';
import { createMinigameInstructionsScreen } from './minigame-instructions';
import { createMinigameCountdownScreen } from './minigame-countdown';
import { createMinigameHUDScreen } from './minigame-hud';
import { createMinigameResultsScreen } from './minigame-results';
import { createPummelAwardsScreen } from './pummel-awards';
import { createGameOverScreen } from './game-over';
import { createErrorModalScreen } from './error-modal';
import { createReconnectingScreen } from './reconnecting';
import { createDisconnectScreen } from './disconnect';
import { createConfirmDialogScreen } from './confirm-dialog';
import { createPauseMenuScreen } from './pause-menu';
import { createHowToPlayScreen } from './how-to-play';
import { createMysteryBoxChoiceScreen } from './mystery-box-choice';
// Plan 24b Phase A board screens
import { createGameHUDScreen } from './game-hud';
import { createTurnStartBannerScreen } from './turn-start-banner';
import { createDiceRollOverlayScreen } from './dice-roll-overlay';
import { createItemTargetingScreen } from './item-targeting';
import { createTileEffectPopupScreen } from './tile-effect-popup';
// Plan 24b Phase B board dialog screens
import { createSoulAnchorScreen } from './soul-anchor';
import { createTreasureChestScreen } from './treasure-chest';
import { createWeaponsCacheScreen } from './weapons-cache';
import { createInventoryViewScreen } from './inventory-view';
import { createMapOverviewScreen } from './map-overview';
import { createScoreboardScreen } from './scoreboard';
import { createRoundSummaryScreen } from './round-summary';
import { createSourceDetector } from '../../input/source-detect';
import type { SourceDetector } from '../../input/source-detect';
import type { CharacterPreviewAdapter } from './character-preview-adapter';
import type { BoardMountPayload } from './board-payload';
import { buildWarmupSubscribeMessage, hasSeenWarmupTutorial } from '../../portal-entry';
import type { AudioSettingsController } from '../../audio-settings';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface BootOpts {
  host: HTMLElement;
  gsapRegistry: GsapRegistry;
  /** Send a message over the WS transport (injected by main client). */
  send: (msg: unknown) => void;
  /** Subscribe to WS messages — returns an unsubscribe function. */
  subscribe: (handler: (msg: unknown) => void) => () => void;
  /** Optional — if omitted, boot creates one via createSourceDetector(). */
  sourceDetector?: SourceDetector;
  /** Production character preview adapter — null if assets failed to load. */
  characterAdapter?: CharacterPreviewAdapter | null;
  /** Optional live 3D camera focus hook for inline character customization. */
  onCustomizeCamera?: (request: CharacterCustomizeCameraRequest) => void;
  /** True once every selectable cosmetic asset is available to Character Select. */
  areCharacterCosmeticsLoaded?: () => boolean;
  /** Background/interactive preload for every selectable cosmetic asset. */
  ensureCharacterCosmeticsLoaded?: () => Promise<void>;
  /** Optional audio callback forwarded into warmup/shared item screens. */
  playSfx?: BoardMountPayload['playSfx'];
  /** Optional hook to show selected item models on the local rig. */
  previewItemInHand?: BoardMountPayload['previewItemInHand'];
  /** Optional hook to clear selected item models from the local rig. */
  clearItemInHandPreview?: BoardMountPayload['clearItemInHandPreview'];
  /** Optional shared board/warmup item-targeting hooks. */
  isActionControlLocked?: BoardMountPayload['isActionControlLocked'];
  subscribeActionControlLock?: BoardMountPayload['subscribeActionControlLock'];
  startItemPlayerTargeting?: BoardMountPayload['startItemPlayerTargeting'];
  startItemDirectionalTargeting?: BoardMountPayload['startItemDirectionalTargeting'];
  startItemRangePreview?: BoardMountPayload['startItemRangePreview'];
  getPlayerNotificationAnchor?: BoardMountPayload['getPlayerNotificationAnchor'];
  /** Optional live audio settings bridge owned by the main client. */
  audioSettings?: AudioSettingsController;
  /** Optional warmup tutorial gamepad bridge owned by the main client. */
  warmupTutorialInput?: WarmupTutorialInputBridge;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Mapping ScreenId → back-nav target on ESC. null = no-op. */
const ESC_NAV: Record<ScreenId, ScreenId | null> = {
  landing: null, // ESC is no-op
  'main-menu': 'warmup-arena-overlay',
  settings: null, // Settings owns Back/ESC so it can honor payload back targets.
  'lobby-host': 'warmup-arena-overlay',
  'lobby-joiner': 'warmup-arena-overlay',
  'lobby-quick-play': 'warmup-arena-overlay',
  'warmup-arena-overlay': null,
  'character-select': 'lobby-host', // default: back to lobby-host
  onboarding: null, // handled specially (sets flag + navigate)
  // Plan 24b Phase A board screens — ESC behaviour TBD (implemented in Tasks 1.2-1.7)
  'board-hud': null,
  'turn-banner': null,
  'dice-roll': null,
  'item-targeting': null,
  'tile-effect-popup': null,
  // Plan 24b Phase B board dialog screens — ESC behaviour handled within each screen factory
  'soul-anchor': null,
  'treasure-chest': null,
  'weapons-cache': null,
  inventory: null,
  'map-overview': null,
  scoreboard: null,
  'round-summary': null,
  // Plan 24c Phase A minigame flow screens — ESC behaviour TBD (implemented in later tasks)
  'minigame-announce': null,
  'minigame-instructions': null,
  'minigame-countdown': null,
  'minigame-hud': null,
  'minigame-results': null,
  'pummel-awards': null,
  'game-over': null,
  // Plan 24c Phase B system overlays
  'error-modal': null,
  reconnecting: null,
  disconnect: null,
  'confirm-dialog': null,
  'pause-menu': null,
  'how-to-play': null,
  'mystery-box-choice': null,
};

const ESC_BACK_TARGET_SCREENS = new Set<ScreenId>([
  'lobby-host',
  'lobby-joiner',
  'lobby-quick-play',
]);

function readEscBackTarget(payload: unknown): ScreenBackTarget | null {
  if (payload == null || typeof payload !== 'object') return null;
  const target = (payload as { readonly escBackTarget?: unknown }).escBackTarget;
  if (target == null || typeof target !== 'object') return null;
  const screenId = (target as { readonly screenId?: unknown }).screenId;
  if (typeof screenId !== 'string' || !(screenId in ESC_NAV)) return null;
  const beforeNavigate = (target as { readonly beforeNavigate?: unknown }).beforeNavigate;
  return {
    screenId: screenId as ScreenId,
    payload: (target as { readonly payload?: unknown }).payload,
    ...(typeof beforeNavigate === 'function'
      ? { beforeNavigate: beforeNavigate as () => void }
      : {}),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function bootPreGameScreens(opts: BootOpts): ScreenRouter {
  const {
    host,
    gsapRegistry,
    send,
    subscribe,
    characterAdapter,
    onCustomizeCamera,
    areCharacterCosmeticsLoaded,
    ensureCharacterCosmeticsLoaded,
    playSfx,
    previewItemInHand,
    clearItemInHandPreview,
    isActionControlLocked,
    subscribeActionControlLock,
    startItemPlayerTargeting,
    startItemDirectionalTargeting,
    startItemRangePreview,
    getPlayerNotificationAnchor,
    audioSettings,
    warmupTutorialInput,
  } = opts;
  const escBackTargets = new Map<ScreenId, ScreenBackTarget>();
  const customizeCameraPayload = onCustomizeCamera == null ? {} : ({ onCustomizeCamera } as const);

  function syncEscBackTarget(screenId: ScreenId, payload: unknown): void {
    if (!ESC_BACK_TARGET_SCREENS.has(screenId)) return;
    const target = readEscBackTarget(payload);
    if (target == null) {
      escBackTargets.delete(screenId);
    } else {
      escBackTargets.set(screenId, target);
    }
  }

  // ── Build registry ──────────────────────────────────────────────────────────
  //
  // Every ScreenId maps to its factory. Lobby factories expect a typed payload
  // (LobbyMountPayload) that includes `send` and `subscribe`; callers of
  // router.navigate('lobby-*', payload) supply those fields themselves. The
  // registry here just wires factories — it doesn't constrain payloads.
  //
  // `send` is consumed by the gamepad join logic (Task 3.3).
  // `subscribe` is retained in the closure for future Tasks that need WS events.
  void subscribe;

  const registry: Record<ScreenId, ScreenFactory<unknown>> = {
    landing: createLandingScreen as ScreenFactory<unknown>,
    'main-menu': ((ctx) => {
      // The legacy main menu can still be mounted manually, so inject
      // send/subscribe from the boot closure here. Otherwise main-menu would
      // pass `undefined` as `subscribe` into lobby payloads → TypeError on mount.
      // Also inject characterAdapter so lobby screens can mount inline character-select.
      const inherited = (ctx.payload as Record<string, unknown> | undefined) ?? {};
      return createMainMenuScreen({
        ...ctx,
        payload: { ...inherited, send, subscribe, characterAdapter, ...customizeCameraPayload },
      });
    }) as ScreenFactory<unknown>,
    settings: ((ctx) => {
      const inherited = (ctx.payload as Record<string, unknown> | undefined) ?? {};
      return createSettingsScreen({
        ...ctx,
        payload: { ...inherited, audioSettings },
      });
    }) as ScreenFactory<unknown>,
    'lobby-host': ((ctx) => {
      syncEscBackTarget('lobby-host', ctx.payload);
      const inherited = (ctx.payload as Record<string, unknown> | undefined) ?? {};
      return createLobbyHostScreen({
        ...ctx,
        payload: {
          ...inherited,
          characterAdapter: inherited.characterAdapter ?? characterAdapter,
          ...customizeCameraPayload,
          areCharacterCosmeticsLoaded,
          ensureCharacterCosmeticsLoaded,
        },
      } as Parameters<typeof createLobbyHostScreen>[0]);
    }) as ScreenFactory<unknown>,
    'lobby-joiner': ((ctx) => {
      syncEscBackTarget('lobby-joiner', ctx.payload);
      const inherited = (ctx.payload as Record<string, unknown> | undefined) ?? {};
      return createLobbyJoinerScreen({
        ...ctx,
        payload: { ...inherited, ...customizeCameraPayload },
      } as Parameters<typeof createLobbyJoinerScreen>[0]);
    }) as ScreenFactory<unknown>,
    'lobby-quick-play': ((ctx) => {
      syncEscBackTarget('lobby-quick-play', ctx.payload);
      return createLobbyQuickPlayScreen(ctx as Parameters<typeof createLobbyQuickPlayScreen>[0]);
    }) as ScreenFactory<unknown>,
    'warmup-arena-overlay': ((ctx) => {
      const inherited = (ctx.payload as Record<string, unknown> | undefined) ?? {};
      return createWarmupArenaOverlayScreen({
        ...ctx,
        payload: {
          ...inherited,
          send,
          subscribe,
          characterAdapter,
          ...customizeCameraPayload,
          areCharacterCosmeticsLoaded,
          ensureCharacterCosmeticsLoaded,
          playSfx,
          previewItemInHand,
          clearItemInHandPreview,
          isActionControlLocked,
          subscribeActionControlLock,
          startItemPlayerTargeting,
          startItemDirectionalTargeting,
          startItemRangePreview,
          getPlayerNotificationAnchor,
          warmupTutorialInput,
        },
      });
    }) as ScreenFactory<unknown>,
    'character-select': createCharacterSelectScreen as ScreenFactory<unknown>,
    onboarding: createOnboardingScreen as ScreenFactory<unknown>,
    // Plan 24b Phase A board screens
    'board-hud': createGameHUDScreen as ScreenFactory<unknown>,
    'turn-banner': createTurnStartBannerScreen as ScreenFactory<unknown>,
    'dice-roll': createDiceRollOverlayScreen as ScreenFactory<unknown>,
    'item-targeting': createItemTargetingScreen as ScreenFactory<unknown>,
    'tile-effect-popup': createTileEffectPopupScreen as ScreenFactory<unknown>,
    // Plan 24b Phase B board dialog screens
    'soul-anchor': createSoulAnchorScreen as ScreenFactory<unknown>,
    'treasure-chest': createTreasureChestScreen as ScreenFactory<unknown>,
    'weapons-cache': createWeaponsCacheScreen as ScreenFactory<unknown>,
    inventory: createInventoryViewScreen as ScreenFactory<unknown>,
    'map-overview': createMapOverviewScreen as ScreenFactory<unknown>,
    scoreboard: createScoreboardScreen as ScreenFactory<unknown>,
    'round-summary': createRoundSummaryScreen as ScreenFactory<unknown>,
    // Plan 24c Phase A minigame flow screens — stubs until later tasks replace them
    'minigame-announce': createMinigameAnnounceScreen as ScreenFactory<unknown>,
    'minigame-instructions': createMinigameInstructionsScreen as ScreenFactory<unknown>,
    'minigame-countdown': createMinigameCountdownScreen as ScreenFactory<unknown>,
    'minigame-hud': createMinigameHUDScreen as ScreenFactory<unknown>,
    'minigame-results': createMinigameResultsScreen as ScreenFactory<unknown>,
    'pummel-awards': createPummelAwardsScreen as ScreenFactory<unknown>,
    'game-over': createGameOverScreen as ScreenFactory<unknown>,
    // Plan 24c Phase B system overlays — stubs until Tasks 4-10 replace them
    'error-modal': createErrorModalScreen as ScreenFactory<unknown>,
    reconnecting: createReconnectingScreen as ScreenFactory<unknown>,
    disconnect: createDisconnectScreen as ScreenFactory<unknown>,
    'confirm-dialog': createConfirmDialogScreen as ScreenFactory<unknown>,
    'pause-menu': createPauseMenuScreen as ScreenFactory<unknown>,
    'how-to-play': createHowToPlayScreen as ScreenFactory<unknown>,
    'mystery-box-choice': createMysteryBoxChoiceScreen as ScreenFactory<unknown>,
  };

  // ── Create router ───────────────────────────────────────────────────────────

  const router = createScreenRouter({ host, gsapRegistry, registry });

  // ── Entry-screen decision ───────────────────────────────────────────────────

  router.navigate('warmup-arena-overlay', { portal: null });

  // ── ESC keydown listener ────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    const current = router.current;
    if (current === null) return;

    if (current === 'onboarding') {
      router.navigate('warmup-arena-overlay', { portal: null });
      return;
    }

    const override = escBackTargets.get(current);
    if (override != null) {
      override.beforeNavigate?.();
      router.navigate(override.screenId, override.payload);
      return;
    }

    if (ESC_BACK_TARGET_SCREENS.has(current)) {
      send({ type: 'leave' });
      send(buildWarmupSubscribeMessage({ isReady: hasSeenWarmupTutorial() }));
      router.navigate('warmup-arena-overlay', { portal: null });
      return;
    }

    const target = ESC_NAV[current];
    if (target !== null) {
      router.navigate(target);
    }
  }

  document.addEventListener('keydown', handleEsc);

  // ── Gamepad press-any-button-to-join ────────────────────────────────────────

  const detector = opts.sourceDetector ?? createSourceDetector();

  // Track which gamepad indices have already joined (dedupe).
  const localPlayers = new Set<number>();

  // Subscribe to gamepad transitions for indices 0..7.
  const gamepadUnsubs: Array<() => void> = [];
  for (let i = 0; i < 8; i++) {
    const index = i;
    const unsub = detector.subscribe(String(index), (source) => {
      if (source !== 'gamepad') return;
      const cur = router.current;
      if (cur !== 'lobby-host' && cur !== 'lobby-joiner') return;
      if (isTouchLobbyLayout()) return;
      if (localPlayers.has(index)) return; // dedupe
      localPlayers.add(index);
      const localSlot = 2 + localPlayers.size - 1;
      send({ type: 'add_local_player', playerName: `Player ${localSlot}`, localSlot });
    });
    gamepadUnsubs.push(unsub);
  }

  // ── Wrapped router with ESC + gamepad cleanup on dispose ────────────────────

  const originalDispose = router.dispose.bind(router);
  const wrappedRouter: ScreenRouter = {
    get current() {
      return router.current;
    },
    get overlayCurrent() {
      return router.overlayCurrent;
    },
    navigate: router.navigate.bind(router),
    clear: router.clear?.bind(router),
    showOverlay: router.showOverlay?.bind(router),
    closeOverlay: router.closeOverlay?.bind(router),
    dispose() {
      document.removeEventListener('keydown', handleEsc);
      gamepadUnsubs.forEach((unsub) => unsub());
      originalDispose();
    },
  };

  return wrappedRouter;
}
